import React, { useState } from 'react';
import { ShoppingBagIcon } from 'lucide-react';
interface Product {
  id: string;
  name: string;
  price: string;
  originalPrice?: string;
  bulkPrice?: string;
  engravingPrice?: string;
  bulkMinimum?: number;
  discount?: string;
  description: string;
  features: string[];
  additionalInfo: string;
}
interface ProductDetailsProps {
  product: Product;
  onAddToOrder: (quantity: number, hasEngraving: boolean, engravingMessage?: string) => void;
  onOrderNow: () => void;
}
const ProductDetails: React.FC<ProductDetailsProps> = ({
  product,
  onAddToOrder,
  onOrderNow
}) => {
  const [quantity, setQuantity] = useState(5);
  const [hasEngraving, setHasEngraving] = useState(false);
  const [engravingMessage, setEngravingMessage] = useState('');
  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };
  const increaseQuantity = () => {
    setQuantity(quantity + 1);
  };
  const isBulkOrder = product.bulkMinimum && quantity >= product.bulkMinimum;
  const showEngravingOption = product.engravingPrice !== undefined;
  return <div className="flex flex-col h-full" data-id="element-81">
      <h1 className="text-2xl md:text-3xl font-light text-gray-800 mb-2" data-id="element-82">
        {product.name}
      </h1>
      <div className="flex flex-col gap-2 mb-4" data-id="element-83">
        {/* Price display with original price and discount */}
        <div className="flex items-center gap-2" data-id="element-84">
          {product.originalPrice && <span className="text-lg text-gray-500 line-through" data-id="element-85">
              {product.originalPrice}
            </span>}
          <span className={`text-xl md:text-2xl font-medium ${isBulkOrder ? 'text-gray-500 line-through' : 'text-gray-800'}`} data-id="element-86">
            {product.price}
          </span>
          {product.discount && <span className="text-sm font-medium text-green-700 bg-green-100 px-2 py-1 rounded" data-id="element-87">
              {product.discount}
            </span>}
          {!isBulkOrder && showEngravingOption && <span className="text-sm text-gray-600" data-id="element-88">
              (+{product.engravingPrice} for personalization)
            </span>}
        </div>
        {/* Bulk pricing */}
        {product.bulkPrice && <div className={`flex items-center gap-2 ${isBulkOrder ? 'text-gray-800' : 'text-gray-600'}`} data-id="element-89">
            <span className={`${isBulkOrder ? 'text-xl md:text-2xl font-medium' : 'text-md'}`} data-id="element-90">
              {product.bulkPrice}
            </span>
            <span className="text-sm" data-id="element-91">
              for {product.bulkMinimum}+ boxes (includes free personalization)
            </span>
            {isBulkOrder && <span className="text-sm font-medium text-green-700 bg-green-100 px-2 py-1 rounded" data-id="element-92">
                Bulk Discount Applied
              </span>}
          </div>}
      </div>
      <p className="text-gray-600 mb-6" data-id="element-93">{product.description}</p>
      <div className="mb-6" data-id="element-94">
        <h3 className="text-lg font-medium text-gray-800 mb-3" data-id="element-95">Features</h3>
        <ul className="space-y-2" data-id="element-96">
          {product.features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-97">
              <span className="text-[#d4b8c7] mr-2" data-id="element-98">•</span>
              <span className="text-gray-600" data-id="element-99">{feature}</span>
            </li>)}
        </ul>
      </div>
      <div className="mb-6" data-id="element-100">
        <h3 className="text-lg font-medium text-gray-800 mb-3" data-id="element-101">Quantity</h3>
        <div className="flex items-center" data-id="element-102">
          <button onClick={decreaseQuantity} className="w-10 h-10 rounded-l border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100" data-id="element-103">
            -
          </button>
          <input type="number" value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 h-10 border-t border-b border-gray-300 text-center text-gray-700" data-id="element-104" />
          <button onClick={increaseQuantity} className="w-10 h-10 rounded-r border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100" data-id="element-105">
            +
          </button>
        </div>
      </div>
      {/* Personal Engraving Option */}
      {showEngravingOption && <div className="mb-6" data-id="element-106">
          <div className="flex items-center gap-2" data-id="element-107">
            <input type="checkbox" id="engraving" checked={hasEngraving} onChange={e => setHasEngraving(e.target.checked)} className="w-4 h-4 text-[#d4b8c7] border-gray-300 rounded focus:ring-[#d4b8c7]" data-id="element-108" />
            <label htmlFor="engraving" className="text-gray-700" data-id="element-109">
              Add personal message engraving
              {!isBulkOrder && hasEngraving && <span className="ml-1 text-sm text-gray-600" data-id="element-110">
                  (+{product.engravingPrice} per box)
                </span>}
              {isBulkOrder && <span className="ml-1 text-sm text-green-700" data-id="element-111">
                  (Free with bulk order)
                </span>}
            </label>
          </div>
          {hasEngraving && <div className="mt-3" data-id="element-112">
              <input type="text" placeholder="Enter your personal message (max 30 characters)" maxLength={30} value={engravingMessage} onChange={e => setEngravingMessage(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-gray-700 text-sm" data-id="element-113" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-114">
                Message will be engraved on the inside of the box lid as shown
                in the image
              </p>
            </div>}
        </div>}
      <div className="mb-6" data-id="element-115">
        <button onClick={() => onAddToOrder(quantity, hasEngraving, hasEngraving ? engravingMessage : undefined)} className="w-full bg-[#752F50] hover:bg-[#c6a6b7] text-white py-3 px-6 rounded-md flex items-center justify-center" data-id="element-116">
          <ShoppingBagIcon className="w-5 h-5 mr-2" data-id="element-117" />
          Add to Order
        </button>
        <div className="mt-2 py-2 px-3 bg-blue-50 border border-blue-100 rounded-md text-center" data-id="element-118">
          <p className="text-blue-800 font-medium text-sm" data-id="element-119">
            No payment required now, we will collect your number in the
            confirmation step
          </p>
        </div>
      </div>
      <div className="mt-auto pt-6 border-t border-gray-200" data-id="element-120">
        <p className="text-sm text-gray-600" data-id="element-121">{product.additionalInfo}</p>
      </div>
    </div>;
};
export default ProductDetails;